<script lang="ts">
import "@cisco-u/modal/cu-modal.js";

import { defineComponent } from "vue";
import { useCookies } from "vue3-cookies";
const cookies = useCookies().cookies;
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

export default defineComponent({
  data() {
    return {
      showTimeoutMsg: false,
    };
  },
  mounted() {
    this.checkSessionTimeout();
    setInterval(() => {
      this.checkSessionTimeout();
    }, 1000); // check every second
  },
  computed: {
    sessionTimeout() {
      const cookieValue = cookies.get("x-lacp-session-timeout");
      return dayjs(cookieValue).utc();
    },
    currentTime() {
      return dayjs(new Date()).utc();
    },
    cdpTimeout() {
      if (globalThis.env.CDP_TIMEOUT) {
        return Number.parseInt(globalThis.env.CDP_TIMEOUT, 10);
      }
      return 2;
    },
    cdpTimeoutIterations() {
      if (globalThis.env.CDP_TIMEOUT_ITERATIONS) {
        return Number.parseInt(globalThis.env.CDP_TIMEOUT_ITERATIONS, 10);
      }
      return 3;
    },
    showSessionTimeoutWarning() {
      return globalThis.env.ENABLE_SESSION_TIMEOUT_WARNING || false;
    },
    sessionTimeoutIntervals() {
      // Set the session timeout intervals based on the session timeout cookie
      const sessionTimeout = this.sessionTimeout;

      let intervals = [] as any[];

      // first interval start 1 hour and 45 minutes after session timeout (start time)
      intervals.push({
        start: sessionTimeout.add(1, "hour").add(45, "minute"),
        end: sessionTimeout
          .add(2, "hour")
          .add(45, "minute")
          .subtract(1, "second"),
      });

      for (let i = 1; i <= this.cdpTimeoutIterations; i++) {
        // start time is always 1 second after the previous end time
        const start = intervals[i - 1].end.add(1, "second");
        // end time is interval * this.cdpTimeout (in hours)
        const end = start.add(this.cdpTimeout, "hour").subtract(1, "second");
        // push the new interval
        intervals.push({
          start,
          end,
        });
      }

      return intervals;
    },
    isInInterval() {
      // Check if the current time is within any of the session timeout intervals
      return this.sessionTimeoutIntervals
        .filter((interval) => {
          return (
            dayjs(this.currentTime).isBetween(
              interval.start,
              interval.end,
              "second",
              "[]"
            ) ||
            dayjs(this.currentTime).isSame(interval.start, "second") ||
            dayjs(this.currentTime).isSame(interval.end, "second")
          );
        })
        .filter((interval) => interval.start && interval.end);
    },
  },
  methods: {
    checkSessionTimeout() {
      if (!this.sessionTimeout || !this.showSessionTimeoutWarning) {
        this.showTimeoutMsg = false;
        return;
      }
      const cdpTimeoutSeen = cookies.get("CDP_TIMEOUT_SEEN");
      // if current time matches within an interval, check if we've already shown warning
      if (
        this.isInInterval.length > 0 &&
        (!cdpTimeoutSeen ||
          cdpTimeoutSeen !== this.isInInterval[0]?.start?.toISOString())
      ) {
        // Show timeout if cookie is not set or if cookie value doesn't match current interval
        this.showTimeoutMsg = true;
      }

      // if current time is past the last interval end time
      if (
        this.sessionTimeoutIntervals.length > 0 &&
        dayjs(this.currentTime).isAfter(
          this.sessionTimeoutIntervals[this.sessionTimeoutIntervals.length - 1]
            .end
        )
      ) {
        this.showTimeoutMsg = true;
      }
    },
    closeTimeoutMsg() {
      this.showTimeoutMsg = false;
      cookies.set("CDP_TIMEOUT_SEEN", this.isInInterval[0].start.toISOString());
    },
  },
});
</script>

<template>
  <div
    v-if="showTimeoutMsg"
    @click="closeTimeoutMsg"
    @keyup.enter="closeTimeoutMsg"
  >
    <cu-modal
      class="session-timeout-modal"
      type="small"
      logoWidth="5rem"
      width="39rem"
      height="20rem"
      :open="showTimeoutMsg"
    >
      <div slot="header">Session Timeout Warning</div>
      <div slot="body">
        <div
          class="mx-auto mt-5 flex max-w-[30.375rem] items-center justify-center text-center font-cisco text-[1.625rem] font-light text-black-darkest"
        >
          <p class="mb-4">Your session is about to expire.</p>
        </div>
      </div>
    </cu-modal>
  </div>
</template>
